/**
 * DashboardPage.js — Dashboard principal
 * Matches the design screenshot: sparkline stat cards, 2-col layout,
 * side-by-side AR/AP donut, bank IBAN rows, 2×2 quick-stat grid, quick-access bar.
 */
import { useState, useEffect } from "react";
import { Link }                from "react-router-dom";
import { useTranslation }      from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, Sector,
  LineChart, Line,
} from "recharts";
import apiFetch           from "../../../shared/services/apiClient";
import { formatCurrency } from "../../../shared/utils/formatCurrency";

/* ─── tiny helpers ────────────────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function useAsync(fn, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fn().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, deps);
  return { data, loading };
}

function useBreakpoint() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return {
    isMobile: w < 640,   // 1 col everything
    isTablet: w < 1024,  // 2 col max
    isWide:   w >= 1024, // 3 col allowed
  };
}

function useIsNarrow(bp = 960) {
  const [narrow, setNarrow] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return narrow;
}

/* ─── Panel wrapper ───────────────────────────────────────────────────────── */
function Panel({ children, style = {} }) {
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border-card)",
      borderRadius: 14,
      padding: "18px 20px",
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── Section title ───────────────────────────────────────────────────────── */
function SectionTitle({ icon, label, color = "#f97316", action }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.82rem" }} />
        <span style={{
          fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.9px",
          textTransform: "uppercase", color: "var(--text-muted)",
        }}>
          {label}
        </span>
      </div>
      {action}
    </div>
  );
}

/* ─── Sparkline ───────────────────────────────────────────────────────────── */
function Sparkline({ data, color, height = 44 }) {
  if (!data?.length) return <div style={{ height }} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2}
          dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ─── Stat card ───────────────────────────────────────────────────────────── */
function StatCard({ label, value, sub, icon, iconBg, iconColor, sparkData, sparkColor }) {
  return (
    <Panel style={{ display: "flex", flexDirection: "column", gap: 4, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.7px",
            textTransform: "uppercase", color: "var(--text-muted)" }}>
            {label}
          </p>
          <p style={{ margin: "5px 0 2px", fontFamily: "monospace", fontWeight: 800,
            fontSize: "1.45rem", color: "var(--text-primary)", lineHeight: 1.1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value}
          </p>
          {sub && <p style={{ margin: 0, fontSize: "0.67rem", color: "var(--text-muted)" }}>{sub}</p>}
        </div>
        <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0,
          background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: "1.05rem" }} />
        </div>
      </div>
      <Sparkline data={sparkData} color={sparkColor} />
    </Panel>
  );
}

/* ─── Custom chart tooltip ────────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label, fmtVal }) {
  if (!active || !payload?.length) return null;
  const fmt = fmtVal ?? ((v) => v.toFixed(0));
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 8, padding: "10px 14px", fontSize: "0.78rem",
      boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
      <p style={{ margin: "0 0 6px", fontWeight: 700, color: "var(--text-primary)" }}>{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.fill || p.stroke }} />
          <span style={{ color: "var(--text-muted)", minWidth: 65 }}>{p.name}:</span>
          <span style={{ color: p.fill || p.stroke, fontWeight: 700, fontFamily: "monospace" }}>
            {fmt(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Ingresos vs Egresos bar chart ───────────────────────────────────────── */
function IngresosEgresosChart({ data, loading, sym, lblIngresos, lblEgresos }) {
  if (loading) return (
    <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--text-muted)", fontSize: 13 }}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
    </div>
  );
  if (!data?.length) return (
    <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--text-muted)", fontSize: 13 }}>—</div>
  );
  const fmtAxis = (v) => {
    const a = Math.abs(v);
    if (a >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000)     return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v}`;
  };
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barGap={3}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-inner)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--text-muted)" }}
          axisLine={false} tickLine={false} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10, fill: "var(--text-muted)" }}
          axisLine={false} tickLine={false} width={44} />
        <Tooltip content={(p) => <ChartTooltip {...p} fmtVal={fmtAxis} />}
          cursor={{ fill: "rgba(255,255,255,0.04)" }} />
        <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 8 }}
          formatter={(v) => <span style={{ color: "var(--text-muted)" }}>{v}</span>} />
        <Bar dataKey="ingresos" name={lblIngresos} fill="#4ade80" radius={[4, 4, 0, 0]} maxBarSize={32} />
        <Bar dataKey="egresos"  name={lblEgresos}  fill="#f87171" radius={[4, 4, 0, 0]} maxBarSize={32} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ─── AR vs AP donut (side-by-side layout) ────────────────────────────────── */
const DONUT_COLORS = ["#f87171", "#fb923c"];

function CxcCxpDonut({ cxcTotal, cxpTotal, cxcCount, cxpCount, fmtK, lblCxc, lblCxp, lblFiliales, lblFacturas }) {
  const [activeIndex, setActiveIndex] = useState(0);

  function renderActiveShape(props) {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
    return (
      <g>
        <text x={cx} y={cy - 10} textAnchor="middle"
          fill="var(--text-primary)" style={{ fontSize: "0.7rem", fontWeight: 700 }}>
          {payload.name}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle"
          fill={fill} style={{ fontSize: "0.85rem", fontWeight: 800, fontFamily: "monospace" }}>
          {fmtK(value)}
        </text>
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 5}
          startAngle={startAngle} endAngle={endAngle} fill={fill} />
      </g>
    );
  }

  const pieData = [
    { name: lblCxc, value: cxcTotal },
    { name: lblCxp, value: cxpTotal },
  ];

  const hasData = cxcTotal > 0 || cxpTotal > 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {/* Donut left */}
      <div style={{ flex: "0 0 150px" }}>
        {hasData ? (
          <ResponsiveContainer width={150} height={150}>
            <PieChart>
              <Pie activeIndex={activeIndex} activeShape={renderActiveShape}
                data={pieData} cx="50%" cy="50%"
                innerRadius={44} outerRadius={60}
                dataKey="value"
                onMouseEnter={(_, i) => setActiveIndex(i)}>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ width: 150, height: 150, display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--text-muted)", fontSize: 12 }}>—</div>
        )}
      </div>

      {/* Legend cards right */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        {[
          { label: "Total CxC", val: cxcTotal, color: "#f87171", sub: `${cxcCount} ${lblFiliales}`, to: "/finanzas/cxc" },
          { label: "Total CxP", val: cxpTotal, color: "#fb923c", sub: `${cxpCount} ${lblFacturas}`, to: "/finanzas/cxp" },
        ].map(({ label, val, color, sub, to }) => (
          <Link key={label} to={to} style={{ textDecoration: "none" }}>
            <div style={{
              background: "var(--bg)", borderRadius: 10, padding: "10px 12px",
              border: "1px solid var(--border-card)", transition: "border-color 0.15s",
            }}
              onMouseOver={e => e.currentTarget.style.borderColor = color}
              onMouseOut={e => e.currentTarget.style.borderColor = "var(--border-card)"}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: "0.6rem", color: "var(--text-muted)", fontWeight: 700,
                    textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                  <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1.05rem", color }}>{fmtK(val)}</div>
                  <div style={{ fontSize: "0.62rem", color: "var(--text-dim)", marginTop: 2 }}>{sub}</div>
                </div>
                <i className="fa-solid fa-people-roof" style={{ color, fontSize: "0.9rem", opacity: 0.5 }} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── Bank account row ────────────────────────────────────────────────────── */
const BANK_COLORS = {
  BCR:        { bg: "rgba(139,92,246,0.15)", color: "#8b5cf6" },
  BNCR:       { bg: "rgba(34,197,94,0.15)",  color: "#22c55e" },
  NACIONAL:   { bg: "rgba(34,197,94,0.15)",  color: "#22c55e" },
  BAC:        { bg: "rgba(96,165,250,0.15)", color: "#60a5fa" },
  DAVIVIENDA: { bg: "rgba(239,68,68,0.15)",  color: "#ef4444" },
  DEFAULT:    { bg: "rgba(96,165,250,0.15)", color: "#60a5fa" },
};

function getBankColor(nombre) {
  const up = (nombre ?? "").toUpperCase();
  const key = Object.keys(BANK_COLORS).find(k => k !== "DEFAULT" && up.includes(k));
  return BANK_COLORS[key ?? "DEFAULT"];
}

function BankRow({ banco, numero_cuenta, saldo, moneda, isUSD, tc }) {
  const saldoNum = Number(saldo ?? 0);
  const { bg, color } = getBankColor(banco);

  // Always show in native currency of the bank account
  const nativeFmt = moneda === "USD"
    ? `$${saldoNum.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
    : `₡${formatCurrency(saldoNum)}`;

  // Currency badge
  const currencyColor = moneda === "USD" ? "#60a5fa" : "#4ade80";

  // IBAN display: show "IBAN: CR12015..." (first 10 chars + "...")
  const ibanLabel = numero_cuenta
    ? `IBAN: ${numero_cuenta.slice(0, 10)}${numero_cuenta.length > 10 ? "..." : ""}`
    : "—";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12,
      padding: "10px 0", borderBottom: "1px solid var(--border-inner)" }}>
      <div style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
        background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className="fa-solid fa-building-columns" style={{ color, fontSize: "0.85rem" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.82rem", color: "var(--text-hover)", fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {banco}
        </div>
        <div style={{ fontSize: "0.66rem", color: "var(--text-muted)", marginTop: 1 }}>
          {ibanLabel}
        </div>
      </div>
      {/* currency badge */}
      <span style={{
        fontSize: "0.58rem", fontWeight: 700, padding: "1px 5px",
        borderRadius: 4, border: `1px solid ${currencyColor}30`,
        color: currencyColor, background: `${currencyColor}12`, flexShrink: 0,
      }}>
        {moneda}
      </span>
      <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.85rem",
        color: saldoNum >= 0 ? "#4ade80" : "#f87171", flexShrink: 0, textAlign: "right" }}>
        {nativeFmt}
      </div>
    </div>
  );
}

/* ─── Quick stat tile (2×2 grid) ─────────────────────────────────────────── */
function QuickStatTile({ icon, label, value, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "12px 14px", background: "var(--bg)", borderRadius: 10,
      border: "1px solid var(--border-card)",
      /* ocupa toda la celda del grid padre para no dejar hueco */
      minHeight: 0,
    }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: `${color}20`,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.8rem" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1.15rem",
          color: "var(--text-primary)", lineHeight: 1.15 }}>
          {value}
        </div>
        <div style={{ fontSize: "0.63rem", color: "var(--text-muted)", lineHeight: 1.3, marginTop: 1 }}>
          {label}
        </div>
      </div>
    </div>
  );
}

/* ─── Invoice row ─────────────────────────────────────────────────────────── */
function InvoiceRow({ factura, fmtFactura }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10,
      padding: "8px 0", borderBottom: "1px solid var(--border-inner)" }}>
      <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0,
        background: "rgba(96,165,250,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className="fa-solid fa-file-invoice" style={{ color: "#60a5fa", fontSize: "0.72rem" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.78rem", color: "var(--text-hover)", fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {factura.concepto || factura.nombre_propietario || "—"}
        </div>
        <div style={{ fontSize: "0.66rem", color: "var(--text-muted)", marginTop: 1 }}>
          {factura.nombre_propietario} · {fmtDate(factura.fecha_emision)}
        </div>
      </div>
      <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.82rem",
        color: factura.moneda === "USD" ? "#60a5fa" : "var(--text-primary)", flexShrink: 0 }}>
        {fmtFactura(factura.monto, factura.moneda)}
      </span>
    </div>
  );
}

/* ─── Quick-access button ─────────────────────────────────────────────────── */
function QuickBtn({ to, icon, label, color }) {
  return (
    <Link to={to} style={{ textDecoration: "none", flex: "1 1 100px", minWidth: 90 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        padding: "11px 8px", background: "var(--bg-card)",
        border: "1px solid var(--border-card)", borderRadius: 12, cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s" }}
        onMouseOver={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = `${color}10`; }}
        onMouseOut={e => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.background = "var(--bg-card)"; }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}18`,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.9rem" }} />
        </div>
        <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--text-secondary)",
          textAlign: "center", lineHeight: 1.3 }}>
          {label}
        </span>
      </div>
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  MAIN DASHBOARD                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const isNarrow = useIsNarrow(960);
  const { isMobile, isTablet } = useBreakpoint();
  const { t, i18n } = useTranslation();
  const langEN     = i18n.language === "en";
  const dateLocale = langEN ? "en-US" : "es-CR";
  // Moneda de visualización de los montos — independiente del idioma. Por
  // defecto sigue el idioma (EN→USD, ES→CRC) pero se puede alternar con el
  // switch ₡/$ del hero sin cambiar el idioma de la interfaz.
  const [monedaVista, setMonedaVista] = useState(langEN ? "USD" : "CRC");
  const isUSD = monedaVista === "USD";
  const sym   = isUSD ? "$" : "₡";

  /* ── fetches ── */
  const { data: tcData } = useAsync(() =>
    apiFetch("/api/finance/tipo-cambio/hoy/").then(r => r.json())
  );
  const { data: facturas } = useAsync(() =>
    apiFetch("/api/finance/factura/?page_size=6&ordering=-fecha_emision").then(r => r.json())
  );
  const { data: cxcData, loading: cxcLoad } = useAsync(() =>
    apiFetch("/api/finance/cuentaXCobrar/?page_size=200").then(r => r.json())
  );
  const { data: cxpData, loading: cxpLoad } = useAsync(() =>
    apiFetch("/api/finance/cuentaXPagar/?page_size=200").then(r => r.json())
  );
  const { data: condominio } = useAsync(() =>
    apiFetch("/api/residents/condominio/").then(r => r.json())
      .then(d => d.results?.[0] ?? d)
  );
  const { data: flujoData, loading: flujoLoad } = useAsync(() =>
    apiFetch("/api/finance/dashboard/flujo-mensual/").then(r => r.json())
  );
  const { data: bancosData } = useAsync(() =>
    apiFetch("/api/finance/cuentaBancaria/").then(r => r.json())
  );
  const { data: filialesData } = useAsync(() =>
    apiFetch("/api/residents/filial/?page_size=1").then(r => r.json())
  );
  const { data: recibosData } = useAsync(() =>
    apiFetch("/api/finance/recibo/?page_size=1").then(r => r.json())
  );

  /* ── derived ── */
  const toArr = (d) => Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
  const tc        = Number(tcData?.tipo_compra) || 1;
  const factList  = toArr(facturas);
  const cxcList   = toArr(cxcData);
  const cxpList   = toArr(cxpData);
  const bancoList = toArr(bancosData);
  const flujoList = toArr(flujoData);

  // saldo_colones = saldo ya normalizado a CRC por el backend — sumar x.saldo
  // directo mezclaría colones y dólares sin convertir cuando hay facturas USD.
  const totalCxC = cxcList.reduce((s, x) => s + Number(x.saldo_colones ?? x.saldo ?? 0), 0);
  const totalCxP = cxpList.reduce((s, x) => s + Number(x.saldo_colones ?? x.saldo ?? 0), 0);

  const totalIngresos6 = flujoList.reduce((s, r) => s + r.ingresos, 0);
  const totalEgresos6  = flujoList.reduce((s, r) => s + r.egresos,  0);
  const superavit      = totalIngresos6 - totalEgresos6;

  // Bank total in CRC (USD accounts converted at TC_compra)
  const totalBancoCRC = bancoList.reduce((s, b) => {
    const sal = Number(b.saldo ?? 0);
    return s + (b.moneda === "USD" ? sal * tc : sal);
  }, 0);

  // This month stats
  const hoy     = new Date();
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
  const factMes = factList.filter(f => (f.fecha_emision ?? "").startsWith(mesActual)).length;
  const filialesCount = filialesData?.count ?? filialesData?.results?.length ?? 0;
  const recibosCount  = recibosData?.count  ?? 0;

  /* ── formatters ── */
  function fmtK(n) {
    const raw = Number(n ?? 0);
    const v   = isUSD ? raw / tc : raw;
    if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 1_000)     return `${sym}${(v / 1_000).toFixed(0)}K`;
    return `${sym}${v.toFixed(0)}`;
  }

  function fmtBankTotal(crcVal) {
    const v = isUSD ? crcVal / tc : crcVal;
    return isUSD
      ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `₡${formatCurrency(v)}`;
  }

  function fmtFactura(monto, moneda = "CRC") {
    const n = Number(monto ?? 0);
    if (isUSD) {
      const usd = moneda === "USD" ? n : n / tc;
      return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (moneda === "USD") return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
    return `₡${formatCurrency(n)}`;
  }

  /* ── sparkline data ── */
  const toUSD = (v) => isUSD ? v / tc : v;
  const sparkIngresos  = flujoList.map(r => ({ v: toUSD(r.ingresos) }));
  const sparkEgresos   = flujoList.map(r => ({ v: toUSD(r.egresos)  }));
  const sparkSuperavit = flujoList.map(r => ({ v: toUSD(r.ingresos - r.egresos) }));
  const sparkBanco     = flujoList.map((r, i) => ({ v: toUSD(totalBancoCRC / (flujoList.length - i + 1)) }));

  /* ── chart data ── */
  const chartData = flujoList.map(r => isUSD
    ? { ...r, ingresos: +(r.ingresos / tc).toFixed(2), egresos: +(r.egresos / tc).toFixed(2) }
    : r
  );

  /* ── i18n labels (idioma de la UI, NO la moneda de visualización) ── */
  const lblIngresos = langEN ? "Income" : "Ingresos";
  const lblEgresos  = langEN ? "Expenses" : "Egresos";
  const lblFiliales = langEN ? "units" : "filiales";
  const lblFacturasLbl = langEN ? "invoices" : "facturas";
  const lblPeriod   = langEN ? "Last 6 months" : "Últimos 6 meses";

  /* ── greeting ── */
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t("dashboard.buenosDias") :
    hour < 19 ? t("dashboard.buenasTardes") :
                t("dashboard.buenasNoches");

  const today = new Date().toLocaleDateString(dateLocale, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <div style={{
        background: "linear-gradient(135deg, var(--bg-card) 0%, var(--bg) 100%)",
        border: "1px solid var(--border-card)", borderRadius: 14,
        padding: isNarrow ? "16px 18px" : "20px 26px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 12, position: "relative", overflow: "hidden",
      }}>
        {/* dot grid decoration */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle, rgba(249,115,22,0.08) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)",
        }} />
        <div>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--text-muted)",
            fontWeight: 500, textTransform: "capitalize" }}>
            {today}
          </p>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.5rem", fontWeight: 800,
            fontFamily: "'Montserrat',sans-serif", color: "var(--text-primary)", letterSpacing: "-0.3px" }}>
            {greeting}{" "}
            <span style={{ color: "#f97316" }}>{condominio?.nombre ?? "Condominio"}</span>
          </h1>
          <p style={{ margin: "5px 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>
            {t("dashboard.resumenFinanciero")}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Switch ₡/$ — moneda de visualización de los montos del dashboard,
              independiente del idioma de la interfaz. Siempre visible, incluso
              en pantallas angostas donde se ocultan los botones de acción. */}
          <div style={{ display: "flex", background: "var(--bg)",
            border: "1px solid var(--border-card)", borderRadius: 8, padding: 2 }}>
            {["CRC", "USD"].map(m => (
              <button key={m} type="button" onClick={() => setMonedaVista(m)}
                title={langEN ? "Display currency" : "Moneda de visualización"}
                style={{
                  padding: "6px 12px", borderRadius: 6, border: "none",
                  background: monedaVista === m ? "#f97316" : "transparent",
                  color: monedaVista === m ? "#fff" : "var(--text-muted)",
                  fontWeight: 700, fontSize: "0.78rem", cursor: "pointer",
                  transition: "background 0.15s, color 0.15s",
                }}>
                {m === "CRC" ? "₡ CRC" : "$ USD"}
              </button>
            ))}
          </div>
          {!isNarrow && (
            <>
              <Link to="/finanzas/facturas/agregar">
                <button style={{ background: "#f97316", border: "none", borderRadius: 8,
                  padding: "9px 18px", color: "#fff", fontWeight: 700, fontSize: "0.82rem",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  <i className="fa-solid fa-plus" />
                  {langEN ? "New Invoice" : "Nueva Factura"}
                </button>
              </Link>
              <Link to="/finanzas/cxp/facturas/agregar">
                <button style={{ background: "transparent", border: "1px solid var(--border-card)",
                  borderRadius: 8, padding: "9px 18px", color: "var(--text-primary)", fontWeight: 600,
                  fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  <i className="fa-solid fa-file-invoice-dollar" />
                  {langEN ? "New AP" : "Nuevo AP"}
                </button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* ══ STAT CARDS ════════════════════════════════════════════════════ */}
      <div style={{ display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12 }}>
        <StatCard
          label={langEN ? "Collected" : "Cobrado"}
          value={fmtK(totalIngresos6)}
          sub={langEN ? "Total collected" : "Total cobrado"}
          icon="fa-wallet"
          iconBg="rgba(139,92,246,0.2)" iconColor="#8b5cf6"
          sparkData={sparkIngresos} sparkColor="#4ade80"
        />
        <StatCard
          label={langEN ? "Paid Out" : "Pagado"}
          value={fmtK(totalEgresos6)}
          sub={langEN ? "Total paid" : "Total pagado"}
          icon="fa-receipt"
          iconBg="rgba(239,68,68,0.2)" iconColor="#ef4444"
          sparkData={sparkEgresos} sparkColor="#f87171"
        />
        <StatCard
          label={langEN ? "Surplus" : "Superávit"}
          value={fmtK(Math.abs(superavit))}
          sub={langEN ? "Net surplus" : "Superávit neto"}
          icon="fa-scale-balanced"
          iconBg={superavit >= 0 ? "rgba(20,184,166,0.2)" : "rgba(248,113,113,0.2)"}
          iconColor={superavit >= 0 ? "#14b8a6" : "#f87171"}
          sparkData={sparkSuperavit} sparkColor={superavit >= 0 ? "#14b8a6" : "#f87171"}
        />
        <StatCard
          label={langEN ? "Bank Balance" : "Saldo Bancario"}
          value={fmtBankTotal(totalBancoCRC)}
          sub={langEN ? "Total balance" : "Saldo total"}
          icon="fa-building-columns"
          iconBg="rgba(234,179,8,0.2)" iconColor="#eab308"
          sparkData={sparkBanco} sparkColor="#eab308"
        />
      </div>

      {/* ══ FILA 1: Ingresos vs Egresos  |  Resumen Rápido ═══════════════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "1fr 300px",
        gap: 16,
        /* stretch: ambos paneles misma altura, sin huecos entre filas */
      }}>

        {/* Ingresos vs Egresos */}
        <Panel>
          <SectionTitle
            icon="fa-chart-column"
            label={langEN ? "Income vs Expenses — Last 6 months" : "Ingresos vs Egresos — Últimos 6 meses"}
            color="#4ade80"
            action={
              <div style={{ display: "flex", alignItems: "center", gap: 6,
                fontSize: "0.68rem", fontWeight: 600, color: "var(--text-muted)",
                background: "var(--bg)", border: "1px solid var(--border-card)",
                borderRadius: 20, padding: "3px 11px", cursor: "default" }}>
                <i className="fa-regular fa-calendar" style={{ fontSize: "0.65rem" }} />
                {lblPeriod}
                <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.55rem" }} />
              </div>
            }
          />
          <IngresosEgresosChart
            data={chartData} loading={flujoLoad} sym={sym}
            lblIngresos={lblIngresos} lblEgresos={lblEgresos}
          />
        </Panel>

        {/* Resumen Rápido — 2×2 grid que llena la altura del panel */}
        <Panel style={{ display: "flex", flexDirection: "column" }}>
          <SectionTitle
            icon="fa-bolt"
            label={langEN ? "Quick Stats" : "Resumen Rápido"}
            color="#c084fc"
          />
          <div style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            gap: 8,
          }}>
            <QuickStatTile
              icon="fa-file-invoice"
              label={langEN ? "Invoices issued this month" : "Facturas emitidas este mes"}
              value={String(factMes || 0)} color="#4ade80"
            />
            <QuickStatTile
              icon="fa-money-bill-transfer"
              label={langEN ? "Payments this month" : "Pagos realizados este mes"}
              value={String(recibosCount || 0)} color="#f87171"
            />
            <QuickStatTile
              icon="fa-building-columns"
              label={langEN ? "Active bank accounts" : "Cuentas bancarias activas"}
              value={String(bancoList.length)} color="#14b8a6"
            />
            <QuickStatTile
              icon="fa-people-roof"
              label={langEN ? "Registered units" : "Filiales registradas"}
              value={filialesCount > 0 ? String(filialesCount) : "—"} color="#fb923c"
            />
          </div>
        </Panel>

      </div>

      {/* ══ FILA 2: Balance CxC vs CxP | Últimas Facturas | Cuentas Bancarias ═ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3, 1fr)",
        gap: 16,
      }}>

        {/* Balance CxC vs CxP — donut, flex-col para llenar altura */}
        <Panel style={{ display: "flex", flexDirection: "column" }}>
          <SectionTitle
            icon="fa-scale-balanced"
            label={langEN ? "AR vs AP Balance" : t("dashboard.balanceCxcVsCxp")}
            color="#f87171"
          />
          <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
            {(cxcLoad || cxpLoad) ? (
              <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--text-muted)", fontSize: 13 }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
              </div>
            ) : (
              <div style={{ width: "100%" }}>
                <CxcCxpDonut
                  cxcTotal={totalCxC} cxpTotal={totalCxP}
                  cxcCount={cxcList.length} cxpCount={cxpList.length}
                  fmtK={fmtK}
                  lblCxc={langEN ? "Receivables" : "Por Cobrar"}
                  lblCxp={langEN ? "Payables" : "Por Pagar"}
                  lblFiliales={lblFiliales} lblFacturas={lblFacturasLbl}
                />
              </div>
            )}
          </div>
        </Panel>

        {/* Últimas Facturas */}
        <Panel>
          <SectionTitle
            icon="fa-file-invoice"
            label={langEN ? "Latest Issued Invoices" : t("dashboard.ultimasFacturas")}
            color="#60a5fa"
            action={
              <Link to="/finanzas/facturas"
                style={{ fontSize: "0.72rem", color: "#f97316", fontWeight: 700, textDecoration: "none" }}>
                {langEN ? "View all →" : "Ver todas →"}
              </Link>
            }
          />
          {factList.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", padding: 16 }}>
              {t("dashboard.noFacturasRecientes")}
            </div>
          ) : (
            factList.slice(0, 5).map((f, i) => (
              <InvoiceRow key={f.id_factura ?? i} factura={f} fmtFactura={fmtFactura} />
            ))
          )}
        </Panel>

        {/* Cuentas Bancarias — flex-col para empujar el total al fondo */}
        <Panel style={{ display: "flex", flexDirection: "column" }}>
          <SectionTitle
            icon="fa-building-columns"
            label={langEN ? "Bank Accounts" : t("dashboard.saldoPorCuentaBancaria")}
            color="#60a5fa"
            action={
              <Link to="/finanzas/catalogo"
                style={{ fontSize: "0.68rem", color: "#60a5fa", fontWeight: 700, textDecoration: "none" }}>
                {langEN ? "View →" : "Ver →"}
              </Link>
            }
          />
          <div style={{ flex: 1 }}>
            {bancoList.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", padding: 10 }}>
                {t("dashboard.sinCuentas")}
              </div>
            ) : (
              bancoList.map((b, i) => (
                <BankRow key={b.id ?? i} {...b} isUSD={isUSD} tc={tc} />
              ))
            )}
          </div>
          {bancoList.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-inner)" }}>
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600 }}>
                {langEN ? "Total bank balance" : "Saldo total en bancos"}
              </span>
              <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "0.92rem", color: "#4ade80" }}>
                {fmtBankTotal(totalBancoCRC)}
              </span>
            </div>
          )}
        </Panel>

      </div>

      {/* ══ QUICK ACCESS BAR ══════════════════════════════════════════════ */}
      <Panel style={{ padding: "14px 16px" }}>
        <SectionTitle icon="fa-star" label={langEN ? "Quick Access" : t("dashboard.accesosRapidos")} color="#f97316" />
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
          <QuickBtn to="/finanzas/facturas/agregar"      icon="fa-file-invoice"         label={langEN ? "New Invoice"    : "Nueva Factura"}   color="#60a5fa" />
          <QuickBtn to="/finanzas/cxp/facturas/agregar"  icon="fa-file-invoice-dollar"  label={langEN ? "New AP"         : "Nuevo AP"}         color="#f87171" />
          <QuickBtn to="/finanzas/recibos/agregar"        icon="fa-receipt"              label={langEN ? "Income Record"  : t("dashboard.registroIngresos")} color="#4ade80" />
          <QuickBtn to="/finanzas/cxc"                    icon="fa-hand-holding-dollar"  label={langEN ? "Receivables"    : "Cuentas por Cobrar"} color="#14b8a6" />
          <QuickBtn to="/finanzas/cxp"                    icon="fa-money-bill-wave"       label={langEN ? "Payables"       : "Cuentas por Pagar"}  color="#fb923c" />
          <QuickBtn to="/finanzas/estados"                icon="fa-scale-balanced"        label={langEN ? "Reports"        : "Reportes"}          color="#c084fc" />
        </div>
      </Panel>

    </div>
  );
}
